/**
 * Following one Brain Session live, over Server-Sent Events.
 *
 * Read with `fetch` because both doors authenticate by a header `EventSource`
 * cannot send, which also makes reconnecting (from the caller's cursor) ours.
 */

/** One recorded output, as the `output` event carries it. */
export interface FollowedOutput {
  sequence: number;
  kind: string;
  output_id: string;
  payload: Record<string, unknown>;
  component_version_id?: string | null;
  created_at: string;
}

/** The reply being written when the follow started, as the `stream` event carries it. */
export interface FollowedStream {
  session_id: string;
  active: boolean;
  turn_event_id: string | null;
  text: string;
  next_index: number;
}

/** One live frame, as the `frame` event carries it (nulls dropped). */
export interface FollowedFrame {
  event: string;
  session_id: string;
  sequence?: number;
  output_id?: string;
  data?: Record<string, unknown>;
}

export interface SessionFollowHandlers {
  onOutput(output: FollowedOutput): void;
  onStream(streamed: FollowedStream): void;
  onFrame(frame: FollowedFrame): void;
  /** The follow is open and the replay is about to arrive. */
  onOpen?(): void;
  /** The server refused the follow; nothing will be retried. */
  onRefused?(status: number): void;
}

export interface SessionFollowOptions {
  /**
   * Open one follow from a cursor: the highest sequence the caller holds with
   * nothing missing before it, or `undefined` for the whole record.
   */
  open(afterSequence: number | undefined, signal: AbortSignal): Promise<Response>;
  /** Asked at every (re)connect, so a reconnect resumes from what is held now. */
  cursor(): number | undefined;
  handlers: SessionFollowHandlers;
}

/** First wait before following again after a drop; doubled per failure. */
const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 15_000;
/** Client errors that are about the moment, not the request: follow again later. */
const RETRIED = new Set([408, 429]);

/**
 * Follow a Session until the returned stop is called. Drops back off and
 * retry; any 4xx but 408/429 is a refusal and ends the follow.
 */
export function followSession({ open, cursor, handlers }: SessionFollowOptions): () => void {
  const controller = new AbortController();
  const { signal } = controller;

  void (async () => {
    let retryMs = FIRST_RETRY_MS;
    while (!signal.aborted) {
      try {
        const response = await open(cursor(), signal);
        if (!response.ok) void response.body?.cancel().catch(() => undefined);
        if (response.status >= 400 && response.status < 500 && !RETRIED.has(response.status)) {
          handlers.onRefused?.(response.status);
          return;
        }
        if (response.ok && response.body) {
          retryMs = FIRST_RETRY_MS;
          handlers.onOpen?.();
          await readEventStream(response.body, signal, (event, data) => dispatch(event, data, handlers));
        }
      } catch {
        // A network error or an abort; the loop decides which.
      }
      if (signal.aborted) return;
      await pause(retryMs, signal);
      retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
    }
  })();

  return () => controller.abort();
}

function dispatch(event: string, data: string, handlers: SessionFollowHandlers): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return;
  }
  if (event === 'output') handlers.onOutput(parsed as FollowedOutput);
  else if (event === 'stream') handlers.onStream(parsed as FollowedStream);
  else if (event === 'frame') handlers.onFrame(parsed as FollowedFrame);
}

/** Read an event stream to its end, calling `onEvent` once per event. */
export async function readEventStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  const abort = () => void reader.cancel().catch(() => undefined);
  signal.addEventListener('abort', abort);
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      let newline = buffer.search(/\r\n|\r|\n/);
      // A trailing `\r` may be the first half of a `\r\n` still in flight.
      while (newline >= 0 && !(buffer[newline] === '\r' && newline === buffer.length - 1)) {
        const line = buffer.slice(0, newline);
        buffer = buffer.slice(buffer[newline] === '\r' && buffer[newline + 1] === '\n' ? newline + 2 : newline + 1);
        if (line === '') {
          // A chunk read just before a stop still holds events nobody wants.
          if (data.length > 0 && !signal.aborted) onEvent(event, data.join('\n'));
          event = 'message';
          data = [];
        } else if (!line.startsWith(':')) {
          const colon = line.indexOf(':');
          const field = colon < 0 ? line : line.slice(0, colon);
          const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
          if (field === 'event') event = value;
          else if (field === 'data') data.push(value);
        }
        newline = buffer.search(/\r\n|\r|\n/);
      }
    }
  } finally {
    signal.removeEventListener('abort', abort);
  }
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const stop = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', stop);
      resolve();
    }, ms);
    signal.addEventListener('abort', stop, { once: true });
  });
}
